import { formatDate } from "../utils/dateUtils";

// ==============================
// FUNCIONES AUXILIARES
// ==============================

// Normaliza la variante (solo permite "full" o "reduced")
export const normalizeVariant = (variant) =>
  variant === "reduced" ? "reduced" : "full";

// Tipos de alerta por fila de listado (pares full/reduced en cada envío)
export const SHIPMENT_WARNING_KIND = {
  TEMP: "temp",
  IMPACT: "impact",
  OPEN: "open",
};

const SHIPMENT_WARNING_FIELD_KEYS = {
  [SHIPMENT_WARNING_KIND.TEMP]: {
    full: "warningsTempFull",
    reduced: "warningsTempReduced",
  },
  [SHIPMENT_WARNING_KIND.IMPACT]: {
    full: "warningsImpactFull",
    reduced: "warningsImpactReduced",
  },
  [SHIPMENT_WARNING_KIND.OPEN]: {
    full: "warningsOpenFull",
    reduced: "warningsOpenReduced",
  },
};

// Cuenta de alertas para un envío según variante de telemetría (full | reduced).

export const getShipmentWarningsByVariant = (record, variant, kind) => {
  const keys = SHIPMENT_WARNING_FIELD_KEYS[kind];
  if (!record || !keys) return 0;
  const v = normalizeVariant(variant);
  const prop = v === "reduced" ? keys.reduced : keys.full;
  const n = record[prop];
  if (n == null) return 0;
  const num = Number(n);
  return Number.isFinite(num) ? num : 0;
};

// Comparador para ordenar filas del listado por alertas (misma variante).
export const compareShipmentWarningsByVariant = (a, b, variant, kind) =>
  getShipmentWarningsByVariant(a, variant, kind) -
  getShipmentWarningsByVariant(b, variant, kind);

//Etiqueta Sí/No según si hay al menos una alerta
export const shipmentWarningYesNoLabel = (count) => (count > 0 ? "Sí" : "No");

// Obtiene la lista de telemetría (Full/Reduced) desde la respuesta del backend
export const getTelemetryListByVariant = (data, variant) => {
  const v = normalizeVariant(variant);
  return v === "reduced"
    ? data.shipmentTelemetryListReduced
    : data.shipmentTelemetryListFull;
};

// Calcula niveles de batería y warnings de batería a partir de una lista de telemetría
export const computeBatteryFromTelemetryList = (
  telemetryList,
  triggerLevel,
) => {
  if (!Array.isArray(telemetryList) || telemetryList.length === 0) {
    return {
      levels: { batteryLvlContainer: null, batteryLvlRemote: null },
      warnings: {
        warningsBatteryLvlContainer: 0,
        warningsBatteryLvlRemote: 0,
      },
    };
  }

  const last = telemetryList[telemetryList.length - 1];
  const batteryLvlContainer = last?.batteryLvlContainer ?? null;
  const batteryLvlRemote = last?.batteryLvlRemote ?? null;

  const threshold =
    typeof triggerLevel === "number" ? triggerLevel : Number(triggerLevel);

  const warningsBatteryLvlContainer =
    batteryLvlContainer != null &&
    Number.isFinite(threshold) &&
    batteryLvlContainer < threshold
      ? 1
      : 0;

  const warningsBatteryLvlRemote =
    batteryLvlRemote != null &&
    Number.isFinite(threshold) &&
    batteryLvlRemote < threshold
      ? 1
      : 0;

  return {
    levels: { batteryLvlContainer, batteryLvlRemote },
    warnings: { warningsBatteryLvlContainer, warningsBatteryLvlRemote },
  };
};

// Convierte una fecha de evento en Date (o null si no existe)
export const getDateFromEvent = (eventDate) =>
  eventDate ? new Date(eventDate) : null;

// Calcula la fecha de inicio con "guard" usando el primer sampleDate de FULL
export const computeStartDateWithGuardFromFull = (data) => {
  const firstSampleDate = data.shipmentTelemetryListFull?.[0]?.sampleDate;
  const startMs = firstSampleDate ? Date.parse(firstSampleDate) : null;
  const minutesToAdd = parseInt(data.periodMinuteGuard, 10) || 0;

  return startMs != null ? startMs + minutesToAdd * 60000 : null;
};

// Determina el tipo de alerta ("alert" | "warning" | null) en base a umbrales de temperatura
export const checkAlertType = (temperature, calcMin, calcMax, tempRisk) => {
  if (temperature < calcMin || temperature > calcMax) return "alert";
  if (temperature >= calcMax - tempRisk || temperature <= calcMin + tempRisk)
    return "warning";
  return null;
};

// Crea el objeto de alerta para el listado (mensaje + fecha formateada)
export const createAlert = (alertType, date) => ({
  class: alertType,
  type: "temperatura",
  message:
    alertType === "alert"
      ? "El contenedor está fuera de los rangos de temperatura establecidos."
      : "La temperatura está cerca de salirse de los rangos establecidos.",
  date: formatDate(date),
});

// Genera el listado de alertas de temperatura sin repetir consecutivas y respetando el guard
export const generateAlerts = (
  telemetryList,
  calcMin,
  calcMax,
  tempRisk,
  startDateWithGuardMs,
) => {
  let lastAlertType = null;

  return telemetryList.reduce((alerts, v) => {
    const sampleMs = Date.parse(v.sampleDate);

    const isWithinRange =
      !startDateWithGuardMs || sampleMs >= startDateWithGuardMs;
    if (!isWithinRange) return alerts;

    const currentAlertType = checkAlertType(
      v.temperature,
      calcMin,
      calcMax,
      tempRisk,
    );

    if (currentAlertType && currentAlertType !== lastAlertType) {
      alerts.push(createAlert(currentAlertType, v.sampleDate));
    }

    lastAlertType = currentAlertType;
    return alerts;
  }, []);
};

// Construye los datos de AlertsList para una variante (full/reduced) incluyendo configTempMin/Max
export const processAlertsListForVariant = (data, variant) => {
  const tempMin = data.tempMin || -50;
  const tempMax = data.tempMax || 50;
  const tempRisk = (tempMax - tempMin) * 0.1;

  const calcMin = tempMin > 50 ? -(tempMin - 50) : tempMin;
  const calcMax = tempMax > 50 ? -(tempMax - 50) : tempMax;

  const startDateWithGuard = computeStartDateWithGuardFromFull(data);
  const telemetryList = getTelemetryListByVariant(data, variant);

  const alertsList = Array.isArray(telemetryList)
    ? generateAlerts(
        telemetryList,
        calcMin,
        calcMax,
        tempRisk,
        startDateWithGuard,
      )
    : [];

  return {
    tempMax: calcMax,
    tempMin: calcMin,
    alertsList,
    configTempMax: data.tempMax,
    configTempMin: data.tempMin,
  };
};

// Crea un mapa de "dots" (alerta de temperatura) usando SOLO la telemetría FULL
// Construye un mapa de dots de alerta por lista de telemetría (por variante)
export const buildTempAlertDotMap = (
  telemetryList,
  calcMin,
  calcMax,
  startDateWithGuardMs,
) => {
  const map = new Map();

  if (!Number.isFinite(calcMin) || !Number.isFinite(calcMax)) return map;

  if (!Array.isArray(telemetryList) || telemetryList.length === 0) return map;

  let lastWasOut = false;

  for (const v of telemetryList) {
    const sampleMs = Date.parse(v.sampleDate);
    const isWithin = !startDateWithGuardMs || sampleMs >= startDateWithGuardMs;
    if (!isWithin) continue;

    let temp = v.temperature;
    if (v.humidity > 100) temp = -v.temperature;

    const isOut = temp < calcMin || temp > calcMax;

    // Dot de alerta solo en la transición "entra en alerta"
    // Si es el primer dato válido de reduced y está fuera, lastWasOut=false -> pinta dot
    if (isOut && !lastWasOut) {
      map.set(formatDate(v.sampleDate), true);
    }

    lastWasOut = isOut;
  }

  return map;
};

// Construye un mapa de dots para un warning booleano (warningImpact, warningOpen, etc.)
// Solo marca el primer registro de una serie consecutiva de warnings
export const buildWarningDotMap = (telemetryList, warningField) => {
  const map = new Map();

  if (!Array.isArray(telemetryList) || telemetryList.length === 0) return map;

  let lastWasActive = false;

  for (const v of telemetryList) {
    const isActive = v[warningField] === true;

    if (isActive && !lastWasActive) {
      map.set(formatDate(v.sampleDate), true);
    }

    lastWasActive = isActive;
  }

  return map;
};

// Mapea la telemetría del backend al formato de Recharts, marcando si hay dots de alerta
export const mapTelemetryListToChartData = (
  list,
  tempDotMap,
  impactDotMap,
  openDotMap,
) => {
  const out = [];
  if (!Array.isArray(list)) return out;

  list.forEach((datum) => {
    let temp = datum.temperature;
    let humidity = datum.humidity;

    if (datum.humidity > 100) {
      temp = -datum.temperature;
      humidity = datum.humidity - 100;
    }

    const date = formatDate(datum.sampleDate);

    out.push({
      date,
      temp,
      humidity,
      hasTempAlertDot: tempDotMap.get(date) === true,
      hasImpactAlertDot: impactDotMap.get(date) === true,
      hasOpenAlertDot: openDotMap.get(date) === true,
    });
  });

  return out;
};

// Construye chartsInfo (telemetryInfo full/reduced + eventos + rangos) desde el detalle del envío
export const buildChartsInfoFromDetail = (data) => {
  let calcMin = data.tempMin;
  let calcMax = data.tempMax;

  if (data.tempMin > 50) calcMin = -(data.tempMin - 50);
  if (data.tempMax > 50) calcMax = -(data.tempMax - 50);

  const firstSampleDate = data.shipmentTelemetryListFull?.[0]?.sampleDate;
  const startDate = getDateFromEvent(firstSampleDate);
  const minutesToAdd = parseInt(data.periodMinuteGuard, 10) || 0;

  const startDateWithGuard = startDate
    ? new Date(startDate.getTime() + minutesToAdd * 60000)
    : null;

  // Mapas de dots para alertas de temperatura (fuera de rango)
  const tempDotMapFull = buildTempAlertDotMap(
    data.shipmentTelemetryListFull,
    calcMin,
    calcMax,
    startDateWithGuard,
  );

  const tempDotMapReduced = buildTempAlertDotMap(
    data.shipmentTelemetryListReduced,
    calcMin,
    calcMax,
    startDateWithGuard,
  );

  // Mapas de dots para alertas de impacto
  const impactDotMapFull = buildWarningDotMap(
    data.shipmentTelemetryListFull,
    "warningImpact",
  );

  const impactDotMapReduced = buildWarningDotMap(
    data.shipmentTelemetryListReduced,
    "warningImpact",
  );

  // Mapas de dots para alertas de apertura
  const openDotMapFull = buildWarningDotMap(
    data.shipmentTelemetryListFull,
    "warningOpen",
  );

  const openDotMapReduced = buildWarningDotMap(
    data.shipmentTelemetryListReduced,
    "warningOpen",
  );

  const telemetryInfoFull = mapTelemetryListToChartData(
    data.shipmentTelemetryListFull,
    tempDotMapFull,
    impactDotMapFull,
    openDotMapFull,
  );

  const telemetryInfoReduced = mapTelemetryListToChartData(
    data.shipmentTelemetryListReduced,
    tempDotMapReduced,
    impactDotMapReduced,
    openDotMapReduced,
  );

  const chartEvents = [];
  if (data?.shipmentMatrixInfoDetail?.allEvents) {
    const {
      preparation,
      preRegistered,
      admitted,
      delivered,
      inReturn,
      returnedToSender,
    } = data.shipmentMatrixInfoDetail.allEvents;

    const eventsSingleShipment = [
      { event: preparation, name: "Preparación pedido", ref: "preparation" },
      { event: preRegistered, name: "Pre-registro", ref: "preRegistered" },
      { event: admitted, name: "Admitido", ref: "admitted" },
      { event: delivered, name: "Entregado", ref: "delivered" },
      { event: inReturn, name: "En devolución", ref: "inReturn" },
      {
        event: returnedToSender,
        name: "Entregado al remitente",
        ref: "returnedToSender",
      },
    ];

    eventsSingleShipment.forEach(({ event, name, ref }) => {
      if (event)
        chartEvents.push({ name, date: formatDate(event.eventDate), ref });
    });
  }

  return {
    telemetryInfo: {
      full: telemetryInfoFull,
      reduced: telemetryInfoReduced,
    },
    events: chartEvents,
    tempMax: calcMax,
    tempMin: calcMin,
    periodMinuteGuard: data.periodMinuteGuard || 0,
    statusCode: 200,
  };
};

// Helpers eventsInfo

const telemetrySampleDateToMs = (sampleDate) => new Date(sampleDate).getTime();

// Encuentra la telemetría más próxima que ocurre DESPUÉS de la fecha del evento
export const findClosestTelemetryAfterEvent = (eventDate, telemetryList) => {
  if (
    !eventDate ||
    !Array.isArray(telemetryList) ||
    telemetryList.length === 0
  ) {
    return null;
  }

  const eventMs = new Date(eventDate).getTime();
  if (Number.isNaN(eventMs)) return null;

  let closestTelemetry = null;
  let smallestDiff = Infinity;

  for (const telemetry of telemetryList) {
    const sampleMs = telemetrySampleDateToMs(telemetry.sampleDate);
    if (Number.isNaN(sampleMs)) continue;

    const diff = sampleMs - eventMs;
    if (diff >= 0 && diff < smallestDiff) {
      smallestDiff = diff;
      closestTelemetry = telemetry;
    }
  }

  return closestTelemetry;
};

// Formatea los valores de temperatura y humedad para mostrar en el step
const formatTelemetryValues = (telemetry) => {
  if (!telemetry) {
    // No hay telemetría posterior al evento; mostramos N/D para no "inventar" valores
    return { temp: "N/D", humidity: "N/D" };
  }

  let temp = telemetry.temperature;
  let humidity = telemetry.humidity;

  if (humidity > 100) {
    temp = 0 - Number(temp);
    humidity = humidity - 100;
  }

  const tempStr = temp != null ? `${temp.toFixed(2)}°C` : "N/D";
  const humStr = humidity != null ? `${humidity.toFixed(2)}%` : "N/D";

  return { temp: tempStr, humidity: humStr };
};

const buildStepFromEvent = (
  title,
  event,
  telemetryList,
  shouldShowTemperature,
  shouldShowHumidity,
) => {
  if (!event) {
    return {
      title,
      description: null,
      location: null,
      temp: null,
      humidity: null,
    };
  }

  let temp = null;
  let humidity = null;

  if (shouldShowTemperature || shouldShowHumidity) {
    const closestTelemetry = findClosestTelemetryAfterEvent(
      event.eventDate,
      telemetryList,
    );
    const telemetryValues = formatTelemetryValues(closestTelemetry);
    temp = shouldShowTemperature ? telemetryValues.temp : null;
    humidity = shouldShowHumidity ? telemetryValues.humidity : null;
  }

  return {
    title,
    description: formatDate(event.eventDate),
    location: event.eventLocation,
    temp,
    humidity,
  };
};

const buildBaseStepDefs = ({
  preparation,
  preRegistered,
  inTransit,
  outForDelivery,
  delivered,
  inReturn,
}) => {
  const hasInReturn = Boolean(inReturn);

  return [
    ...(preparation
      ? [
          {
            title: "Preparación pedido",
            event: preparation,
            isFinalEvent: false,
          },
        ]
      : []),

    { title: "Pre-registro", event: preRegistered, isFinalEvent: false },
    { title: "En camino", event: inTransit, isFinalEvent: false },
    { title: "En reparto", event: outForDelivery, isFinalEvent: false },

    // Entregado solo si NO hay devolución (es evento final)
    ...(hasInReturn
      ? []
      : [{ title: "Entregado", event: delivered, isFinalEvent: true }]),
  ];
};

const buildReturnStepsIfNeeded = (
  { inReturn, returnedToSender },
  telemetryList,
  shouldShowTemperature,
  shouldShowHumidity,
) => {
  const hasInReturn = Boolean(inReturn);
  if (!hasInReturn) return [];

  return [
    buildStepFromEvent(
      "En devolución",
      inReturn,
      telemetryList,
      shouldShowTemperature,
      shouldShowHumidity,
    ),
    buildStepFromEvent(
      "Entregado al remitente",
      returnedToSender ?? null,
      telemetryList,
      shouldShowTemperature,
      shouldShowHumidity,
    ),
  ];
};

const findCurrentFromSteps = (steps) => {
  for (let i = steps.length - 1; i >= 0; i--) {
    if (steps[i]?.description) return i;
  }
  return 0;
};

// Construye eventsInfo (steps + current) desde los eventos del detalle del envío
export const buildEventsInfoFromDetail = (data) => {
  const allEvents = data?.shipmentMatrixInfoDetail?.allEvents;
  if (!allEvents) return { steps: [], current: 0 };

  const {
    preparation,
    preRegistered,
    inTransit,
    outForDelivery,
    delivered,
    inReturn,
    returnedToSender,
  } = allEvents;

  // Usar telemetría Full para obtener los valores de temperatura y humedad
  const telemetryList = data.shipmentTelemetryListFull || [];
  const telemetryTypeCode = data?.telemetryTypeCode;
  const shouldShowTemperature =
    telemetryTypeCode === 4 || telemetryTypeCode === 5;
  const shouldShowHumidity = telemetryTypeCode === 4;

  const baseDefs = buildBaseStepDefs({
    preparation,
    preRegistered,
    inTransit,
    outForDelivery,
    delivered,
    inReturn,
  });

  const baseSteps = baseDefs.map(({ title, event }) =>
    buildStepFromEvent(
      title,
      event,
      telemetryList,
      shouldShowTemperature,
      shouldShowHumidity,
    ),
  );

  const returnSteps = buildReturnStepsIfNeeded(
    { inReturn, returnedToSender },
    telemetryList,
    shouldShowTemperature,
    shouldShowHumidity,
  );

  const steps = [...baseSteps, ...returnSteps];
  const current = findCurrentFromSteps(steps);

  return { steps, current };
};

// Construye shipmentData para el listado a partir de la respuesta del backend (incluye paginación)
export const buildShipmentDataFromListResponse = (
  response,
  fallbackSize,
  fallbackPage,
) => {
  const responseOk = response.status === 200;

  const list = responseOk ? (response.data.shipmentList?.data ?? []) : [];
  const processed = list.map((shipment, index) => ({
    ...shipment,
    key: index,
  }));

  const shipmentDataState = responseOk
    ? {
        status: "loaded",
        data: processed,
        statusCode: 200,
        message: "",
        size: response.data.shipmentList.pageSize,
        page: (response.data.shipmentList.pageNumber ?? 0) + 1,
        totalResults: response.data.shipmentList.totalResults,
        totalPages: response.data.shipmentList.totalPages,
      }
    : {
        status: "loaded",
        data: [],
        statusCode: response.status,
        message: "No se han encontrado datos.",
        size: fallbackSize,
        page: fallbackPage,
        totalResults: 0,
        totalPages: 0,
      };

  return { shipmentDataState, responseOk };
};

// Construye el patch de telemetryInfo (listado) SOLO si vienen alertTotalFull/Reduced
export const buildTelemetryInfoPatchFromListResponse = (data) => {
  const hasAlerts = Boolean(data?.alertTotalFull || data?.alertTotalReduced);
  if (!hasAlerts) return null;

  return {
    full: {
      warnings: {
        warningsImpact: data.alertTotalFull?.warningsImpactTotal ?? 0,
        warningsOpen: data.alertTotalFull?.warningsOpenTotal ?? 0,
        warningsTemp: data.alertTotalFull?.warningsTempTotal ?? 0,
        warningsHum: data.alertTotalFull?.warningsHumTotal ?? 0,
      },
    },
    reduced: {
      warnings: {
        warningsImpact: data.alertTotalReduced?.warningsImpactTotal ?? 0,
        warningsOpen: data.alertTotalReduced?.warningsOpenTotal ?? 0,
        warningsTemp: data.alertTotalReduced?.warningsTempTotal ?? 0,
        warningsHum: data.alertTotalReduced?.warningsHumTotal ?? 0,
      },
    },
    statusCode: 200,
  };
};

// Construye el estado base de singleShipmentData desde el detalle del envío
export const buildSingleShipmentDataFromDetail = (data, status) => {
  return {
    file: data.file,
    standAlone: data.standalone,
    statusCode: status,
    status: "loaded",
    telemetryTypeCode: data.telemetryTypeCode,
    imei: data.imei,
    macMando: data.macMando,
    numRefClient: data.shipmentMatrixInfoDetail.numRefClient,
    destinationData: {
      destinationName: data.shipmentMatrixInfoDetail.destinationName,
      destinationStreet: data.shipmentMatrixInfoDetail.destinationStreet,
      destinationPostalCode:
        data.shipmentMatrixInfoDetail.destinationPostalCode,
      destinationCity: data.shipmentMatrixInfoDetail.destinationCity,
      destinationProvince: data.shipmentMatrixInfoDetail.destinationProvince,
    },
  };
};

// Construye batteryLevels + batteryWarnings por variante desde el detalle del envío
export const buildBatteryStateFromDetail = (data) => {
  const fullBattery = computeBatteryFromTelemetryList(
    data.shipmentTelemetryListFull,
    data.batteryAlarmTriggerLevel,
  );

  const reducedBattery = computeBatteryFromTelemetryList(
    data.shipmentTelemetryListReduced,
    data.batteryAlarmTriggerLevel,
  );

  return {
    batteryLevels: {
      full: fullBattery.levels,
      reduced: reducedBattery.levels,
    },
    batteryWarningsByVariant: {
      full: fullBattery.warnings,
      reduced: reducedBattery.warnings,
    },
  };
};

// Construye el patch de telemetryInfo (detalle) SOLO si existen alertTotalFull/Reduced (incluye batteryWarnings)
export const buildTelemetryInfoPatchFromDetail = (
  data,
  batteryWarningsByVariant,
  status,
) => {
  const hasAlerts = Boolean(data?.alertTotalFull || data?.alertTotalReduced);
  if (!hasAlerts) return null;

  return {
    full: {
      warnings: {
        warningsImpact: data.alertTotalFull?.warningsImpactTotal ?? 0,
        warningsOpen: data.alertTotalFull?.warningsOpenTotal ?? 0,
        warningsTemp: data.alertTotalFull?.warningsTempTotal ?? 0,
        warningsHum: data.alertTotalFull?.warningsHumTotal ?? 0,
      },
      batteryWarnings: batteryWarningsByVariant.full,
    },
    reduced: {
      warnings: {
        warningsImpact: data.alertTotalReduced?.warningsImpactTotal ?? 0,
        warningsOpen: data.alertTotalReduced?.warningsOpenTotal ?? 0,
        warningsTemp: data.alertTotalReduced?.warningsTempTotal ?? 0,
        warningsHum: data.alertTotalReduced?.warningsHumTotal ?? 0,
      },
      batteryWarnings: batteryWarningsByVariant.reduced,
    },
    statusCode: status,
  };
};

// Construye alertsListInfo por variante (full/reduced) desde el detalle del envío
export const buildAlertsListInfoFromDetail = (data, status) => {
  const alertsFull = processAlertsListForVariant(data, "full");
  const alertsReduced = processAlertsListForVariant(data, "reduced");

  return {
    full: alertsFull,
    reduced: alertsReduced,
    statusCode: status,
  };
};

// Construye chartsInfo completo para guardarlo en estado (incluye statusCode)
export const buildChartsInfoStateFromDetail = (data, status) => {
  const charts = buildChartsInfoFromDetail(data);
  return { ...charts, statusCode: status };
};

// Determina el tipo de envío: 'online', 'standalone' u 'offline'
export const getShipmentType = (standalone, file) => {
  if (!standalone) return "online";
  if (file !== null && file !== undefined) return "offline";
  return "standalone";
};

// Construye eventsInfo completo para guardarlo en estado (incluye statusCode y shipmentType)
export const buildEventsInfoStateFromDetail = (data, statusCode) => {
  const events = buildEventsInfoFromDetail(data);
  const shipmentType = getShipmentType(data.standalone, data.file);
  return {
    ...events,
    statusCode,
    shipmentType,
    telemetryTypeCode: data?.telemetryTypeCode,
  };
};

// Construye todos los bloques de estado del detalle (singleShipmentData + battery + alertsList + charts + events + patch telemetryInfo)
export const buildAllDetailState = (data, status) => {
  const singleShipmentData = buildSingleShipmentDataFromDetail(data, status);

  const { batteryLevels, batteryWarningsByVariant } =
    buildBatteryStateFromDetail(data);

  const telemetryInfoPatch = buildTelemetryInfoPatchFromDetail(
    data,
    batteryWarningsByVariant,
    status,
  );

  const alertsListInfo = buildAlertsListInfoFromDetail(data, status);
  const chartsInfo = buildChartsInfoStateFromDetail(data, status);
  const eventsInfo = buildEventsInfoStateFromDetail(data, 200);

  return {
    singleShipmentData,
    batteryLevels,
    telemetryInfoPatch,
    alertsListInfo,
    chartsInfo,
    eventsInfo,
  };
};
